import { useEffect, useMemo, useState } from "react";
import { useRequest } from "../hooks/useRequest";
import {
  getOrders,
  exportOrdersCsv,
  OrdersQuery,
  OriginalOrder,
} from "../api/orders";
import { formatCents, formatDateTime, formatNumber } from "../utils/format";
import OrderDetailDrawer from "./OrderDetailDrawer";
import "./OriginalOrders.scss";

/** 每页可选条数 */
const PAGE_SIZES = [20, 50, 100];

/**
 * 状态下拉选项（value 为传给后端的 status 枚举，"" 表示全部）。
 * 枚举与后端订单状态一一对应，按订单生命周期顺序排列。
 */
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All Status" },
  { value: "RECEIVED", label: "Received" },
  { value: "AWAIT_PAYMENT", label: "Awaiting Payment" },
  { value: "PAYMENT_RECEIVED", label: "Payment Received" },
  { value: "AWAIT_CONCIERGE", label: "Awaiting Concierge" },
  { value: "ORDERBOT_REDUX", label: "Placing Order" },
  { value: "FAILED_TO_PLACE", label: "Failed to Place" },
  { value: "MERCHANT_PROCESSING", label: "Merchant Processing" },
  { value: "MERCHANT_EXCEPTION", label: "Merchant Exception" },
  { value: "SHIPPED", label: "Shipped" },
  { value: "DOMESTIC_DELIVERED", label: "Domestic Delivered" },
  { value: "DELIVERED", label: "Delivered" },
];

/** 已知状态枚举 → 展示文案；未知的走 prettify 兜底 */
const STATUS_LABELS: Record<string, string> = {
  RECEIVED: "Received",
  AWAIT_PAYMENT: "Awaiting Payment",
  PAYMENT_RECEIVED: "Payment Received",
  AWAIT_CONCIERGE: "Awaiting Concierge",
  ORDERBOT_REDUX: "Placing Order",
  FAILED_TO_PLACE: "Failed to Place",
  MERCHANT_PROCESSING: "Merchant Processing",
  MERCHANT_EXCEPTION: "Merchant Exception",
  SHIPPED: "Shipped",
  DOMESTIC_DELIVERED: "Domestic Delivered",
  DELIVERED: "Delivered",
};

/** 枚举 → 友好文案：优先查表，否则把 AWAIT_CONCIERGE 之类转成 "Await Concierge" */
function statusLabel(status: string): string {
  if (!status) return "";
  const known = STATUS_LABELS[status.toUpperCase()];
  if (known) return known;
  return status
    .toLowerCase()
    .split(/[_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** 状态 → 颜色类名（按关键字匹配枚举） */
function statusClass(status: string): string {
  const s = status.toUpperCase();
  if (/DELIVER|COMPLETE|SUCCESS|SIGNED/.test(s)) {
    return "cs-orders__status--success";
  }
  if (/SHIP|TRANSIT|FULFILL|DISPATCH/.test(s)) {
    return "cs-orders__status--shipped";
  }
  if (/CANCEL|FAIL|ERROR|REJECT|REFUND|EXCEPTION/.test(s)) {
    return "cs-orders__status--danger";
  }
  if (
    /AWAIT|PENDING|PROCESS|CONCIERGE|PLACED|PAID|PAYMENT|RECEIV|ORDERBOT|REDUX|CONFIRM/.test(
      s
    )
  ) {
    return "cs-orders__status--processing";
  }
  return "cs-orders__status--default";
}

/** 空值占位 */
const DASH = "—";

/** 把 <input type="date"> 的 yyyy-mm-dd 转成 ISO instant（含边界时刻） */
function dateToInstant(date: string, endOfDay = false): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T${endOfDay ? "23:59:59" : "00:00:00"}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Date → yyyy-mm-dd（本地时区，供 <input type="date"> 使用） */
function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 默认日期范围：最近 30 天（含今天） */
const DEFAULT_RANGE_DAYS = 30;
function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { from: toDateInput(from), to: toDateInput(to) };
}

/** 生成分页页码序列（含省略号）：1 … (c-1) c (c+1) … last */
function buildPageList(current: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push("…");
  pages.push(totalPages);
  return pages;
}

const OriginalOrders = () => {
  useEffect(() => {
    document.title = "Original Orders | CloudStore API";
  }, []);

  // 默认日期范围：最近 30 天（在挂载时计算一次）
  const [initialRange] = useState(defaultDateRange);

  // 已提交（生效中）的查询条件：默认带上最近 30 天，列表与导出都跟随该范围
  const [query, setQuery] = useState<OrdersQuery>(() => ({
    page: 1,
    size: 20,
    from: dateToInstant(initialRange.from),
    to: dateToInstant(initialRange.to, true),
  }));

  // 表单输入（未点 Search 前不生效）
  const [bxlId, setBxlId] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  // 当前打开详情弹窗的订单号（null 表示未打开）
  const [detailOrderId, setDetailOrderId] = useState<string | null>(null);

  const { data, loading, error } = useRequest(
    () => getOrders(query),
    [query]
  );

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const page = query.page ?? 1;
  const size = query.size ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / size));

  const rangeStart = total === 0 ? 0 : (page - 1) * size + 1;
  const rangeEnd = Math.min(page * size, total);

  const pageList = useMemo(
    () => buildPageList(page, totalPages),
    [page, totalPages]
  );

  /** 点击 Search：收集表单，回到第 1 页 */
  const handleSearch = () => {
    setQuery({
      page: 1,
      size,
      search: bxlId.trim() || undefined,
      merchantOrderId: merchantId.trim() || undefined,
      status: status || undefined,
      from: dateToInstant(dateFrom),
      to: dateToInstant(dateTo, true),
    });
  };

  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    setQuery((q) => ({ ...q, page: p }));
  };

  const changeSize = (newSize: number) => {
    setQuery((q) => ({ ...q, size: newSize, page: 1 }));
  };

  const handleExport = async () => {
    setExportErr(null);
    setExporting(true);
    try {
      const blob = await exportOrdersCsv({
        from: dateToInstant(dateFrom),
        to: dateToInstant(dateTo, true),
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "orders-export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const renderAmount = (cents: number | null): string =>
    cents == null ? DASH : formatCents(cents);

  const renderDateTime = (iso: string | null): string =>
    iso ? formatDateTime(iso) : DASH;

  return (
    <div className="cs-orders">
      <h1 className="cs-orders__title">Original Orders</h1>

      {/* 筛选栏 */}
      <div className="cs-orders__filters">
        <input
          className="cs-orders__input"
          placeholder="BXL Order ID"
          value={bxlId}
          onChange={(e) => setBxlId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <input
          className="cs-orders__input"
          placeholder="Merchant Order ID"
          value={merchantId}
          onChange={(e) => setMerchantId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />

        <div className="cs-orders__field">
          <span className="cs-orders__field-label">Status</span>
          <select
            className="cs-orders__select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="cs-orders__field">
          <span className="cs-orders__field-label">Created At</span>
          <input
            type="date"
            className="cs-orders__date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <span className="cs-orders__date-sep">–</span>
          <input
            type="date"
            className="cs-orders__date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="cs-orders__btn cs-orders__btn--primary"
          onClick={handleSearch}
        >
          Search
        </button>
        <button
          type="button"
          className="cs-orders__btn cs-orders__btn--primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {exportErr && <div className="cs-orders__error">{exportErr}</div>}

      {/* 表格 */}
      <div className="cs-orders__table-wrap">
        <table className="cs-orders__table">
          <thead>
            <tr>
              <th className="cs-orders__th">BXL Order ID</th>
              <th className="cs-orders__th">Merchant Order ID</th>
              <th className="cs-orders__th">Created At</th>
              <th className="cs-orders__th">Actual Amount</th>
              <th className="cs-orders__th">Status</th>
              <th className="cs-orders__th">Order Price</th>
              <th className="cs-orders__th">Package No.</th>
              <th className="cs-orders__th">Carrier</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="cs-orders__state" colSpan={9}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td className="cs-orders__state cs-orders__state--error" colSpan={9}>
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && orders.length === 0 && (
              <tr>
                <td className="cs-orders__state" colSpan={9}>
                  No orders found.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              orders.map((o: OriginalOrder, i) => (
                <tr key={o.orderId || i}>
                  <td className="cs-orders__td cs-orders__id">
                    {o.orderId ? (
                      <button
                        type="button"
                        className="cs-orders__id-link"
                        onClick={() => setDetailOrderId(o.orderId)}
                      >
                        {o.orderId}
                      </button>
                    ) : (
                      DASH
                    )}
                  </td>
                  <td className="cs-orders__td">{o.merchantOrderId || DASH}</td>
                  <td className="cs-orders__td">{renderDateTime(o.createdAt)}</td>
                  <td className="cs-orders__td cs-orders__amount">
                    {renderAmount(o.actualAmountCents)}
                  </td>
                  <td className="cs-orders__td">
                    {o.status ? (
                      <span
                        className={`cs-orders__status ${statusClass(o.status)}`}
                        title={o.status}
                      >
                        {statusLabel(o.status)}
                      </span>
                    ) : (
                      DASH
                    )}
                  </td>
                  <td className="cs-orders__td">
                    {renderAmount(o.orderPriceCents)}
                  </td>
                  <td className="cs-orders__td">{o.packageNo || DASH}</td>
                  <td className="cs-orders__td">{o.carrier || DASH}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* 底部：每页条数 + 计数 + 分页 */}
      <div className="cs-orders__footer">
        <div className="cs-orders__footer-left">
          <span className="cs-orders__per-page-label">per page</span>
          <select
            className="cs-orders__per-page"
            value={size}
            onChange={(e) => changeSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="cs-orders__count">
            Showing {formatNumber(rangeStart)}-{formatNumber(rangeEnd)} of{" "}
            {formatNumber(total)} orders
          </span>
        </div>

        <div className="cs-orders__pager">
          <button
            type="button"
            className="cs-orders__page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>
          {pageList.map((p, idx) =>
            p === "…" ? (
              <span key={`gap-${idx}`} className="cs-orders__page-gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`cs-orders__page-btn${
                  p === page ? " cs-orders__page-btn--active" : ""
                }`}
                onClick={() => goToPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="cs-orders__page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>

      {/* 订单详情弹窗（仅在有订单号时挂载，避免关闭后以 null 触发请求） */}
      {detailOrderId && (
        <OrderDetailDrawer
          orderId={detailOrderId}
          onClose={() => setDetailOrderId(null)}
        />
      )}
    </div>
  );
};

export default OriginalOrders;
